// Route-derived organization identity and the committed-organization
// liveness store.
//
// This module is NOT an ambient organization source for requests. Request
// tenancy is an explicit argument (see identity.ts and the scoped
// transports); the committed value here exists so operation liveness
// (operation.ts), the entry guard, and the organization-scoped React
// boundary can observe organization changes and abort or discard work that
// belongs to a previous organization. Unmigrated legacy callers keep using
// utils.helpers.getCurrentOrganization() (the live-URL seam) until step 8
// deletes it; nothing in src/session re-exports an ambient getter.

// The derived segment becomes the X-Astur-Organization header value, where
// control characters and non-Latin-1 make Headers.set/setRequestHeader throw
// a plain TypeError on every request (Bugsnag noise, not a session
// transition). Reject those at the derivation boundary instead: null is the
// well-handled "no organization" state. Printable ASCII plus printable
// Latin-1 stays permissive enough for every name the backend can serve.
export const HEADER_SAFE_SEGMENT = /^[\x20-\x7E\xA0-\xFF]+$/;

const firstPathSegment = (asPath: string): string | null => {
  const path = asPath.split(/[?#]/, 1)[0];
  const segment = path.split('/').filter(Boolean)[0];
  if (!segment) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    decoded = segment;
  }
  return HEADER_SAFE_SEGMENT.test(decoded) ? decoded : null;
};

export const isOrgRoutePattern = (routePattern: string): boolean =>
  typeof routePattern === 'string' && (routePattern === '/[sitename]' || routePattern.startsWith('/[sitename]/'));

export const deriveOrganization = (routePattern: string, asPath: string): string | null => {
  if (!isOrgRoutePattern(routePattern)) return null;
  return firstPathSegment(asPath);
};

// Client-only URL fallback for OrganizationScope's pre-ready render: before
// the router is ready nothing is committed, and without this the scope key
// would flip from the placeholder to the real organization when the router
// becomes ready, remounting the subtree on every cold load. Keyed on the
// resolved route pattern - the pattern is known before the router is ready,
// so a non-organization page (including a client 404) never briefly commits
// its first URL segment as an organization. This is not an ambient
// request-identity source - requests never read it.
export const deriveClientUrlOrganizationFallback = (routePattern: string): string | null => {
  if (typeof window === 'undefined' || !isOrgRoutePattern(routePattern)) return null;
  return firstPathSegment(window.location.pathname);
};

let committedOrganization: string | null = null;
let hasCommitted = false;
let organizationGeneration = 0;
let organizationController = typeof AbortController !== 'undefined' ? new AbortController() : (null as unknown as AbortController);
const organizationListeners = new Set<() => void>();

// The committed organization: the value the mounted organization-scoped
// subtree was rendered for. Published from OrganizationScope's layout effect.
// Consumers are liveness checks and the entry guard - never request headers.
export const getCommittedOrganization = (): string | null => committedOrganization;
export const hasCommittedOrganizationIdentity = (): boolean => hasCommitted;
export const getOrganizationGeneration = (): number => organizationGeneration;
export const getOrganizationSignal = (): AbortSignal => organizationController.signal;

export const subscribeOrganizationIdentity = (listener: () => void): (() => void) => {
  organizationListeners.add(listener);
  return () => organizationListeners.delete(listener);
};

export const publishCommittedOrganization = (organization: string | null): void => {
  if (typeof window === 'undefined') return;
  if (hasCommitted && organization === committedOrganization) return;

  const previousController = organizationController;

  committedOrganization = organization;
  hasCommitted = true;
  organizationGeneration += 1;
  organizationController = new AbortController();
  previousController.abort();
  for (const listener of organizationListeners) listener();
};

// Test-only: restores the pristine "nothing committed" state, aborting
// outstanding operation signals like a normal organization change. The shared
// unit-test teardown imports it directly; production code must never reset
// session identity.
export const __resetCommittedOrganizationForTests = (): void => {
  organizationController.abort();
  committedOrganization = null;
  hasCommitted = false;
  organizationGeneration += 1;
  organizationController = new AbortController();
  for (const listener of organizationListeners) listener();
};

// Test-only reset used by the shared unit-test teardown. Subscriptions are
// normally removed by React unmount, but a file that forgets cleanup() leaks
// its subscriber for the worker's lifetime, and every later notification then
// fires a React update on an unmounted tree, attributing act warnings to the
// wrong file.
export const __resetOrganizationIdentityListenersForTests = (): void => {
  organizationListeners.clear();
};
